import { hasError, showError } from "../../features/actions";
import { prepareChatMessagesForProject } from "../../features/chatrequests";
import { getFileContent } from "../../features/http";
import { triggerIntegrationStyles } from "../../features/integration";
import { saveProjectInLocalProjectList } from "../../features/localstorage/saver";
import { getQueryParams } from "../../features/queryparams";
import { loadProjectJson, loadQuestJson } from "../../features/requests";
import { getAppCfg, saveQueryParamsToAppConfiguration, setAppCfg } from "../app-cfg";
import { loadProjectList } from "../code/loader";
import { saveUserData } from "../code/saver";
import { deleteUserCodeFromLS } from "../../features/localstorage/clean";

import { CHAT_VIEW_NAME, CONTENT_VIEW_NAME, EDITOR_VIEW_NAME, ERROR_VIEW_NAME, LECTURE_VIEW_NAME } from "../constants";
import { createProjectUrl, getProjectMetaFromCloudId, isCloudProjectId } from "../project/projectid";
import { loadContentProject, loadEditorProject, loadInteractiveLecture, showLoadingView } from "../project/projectloader";
import { getStartCode } from "../../features/coderunner";
import { shouldUseVFS, vfs } from "../../features/filesystem";
import { createQuestUrl } from "../quest/questid";
import { getQuestDataForId, loadQuestData } from "../quest/questloader";
import { getDomainCfg } from "../state";
import { ProjectData, QueryParams } from "../types";
import { getViewData, setViewData, showView } from "../views";
import { getNewTemplateProjectId } from "./projectslist";

export async function loadData(publicProject: boolean): Promise<void> {

  const queryParams: QueryParams = await getQueryParams();
  if (Object.keys(queryParams).length === 0) {
    return;
  }

  saveQueryParamsToAppConfiguration(queryParams);
  if (isViewWithProject()) {
    await loadProject(publicProject);
  } else if (isViewWithProjectList()) {
    await loadProjectList();
    showView(getAppCfg().view);
  } else if (isViewWithQuest()) {
    await loadQuest();
    showView(getAppCfg().view);
  } else {
    showView(getAppCfg().view);
  }
}

export async function showNextProject(projectCode: string): Promise<void> {
  showLoadingView();
  const appCfg = getAppCfg();
  appCfg.projectId = projectCode;
  setAppCfg(appCfg);
  await loadProject(false);
}

export async function showPromptedProject(llmAnswer: any, query: string): Promise<void> {
  showLoadingView();
  const newProjectId = await getNewTemplateProjectId(llmAnswer.projectId.toUpperCase());
  const uppProjectId = newProjectId.toUpperCase();

  const url = new URL(window.location.href);
  const params = new URLSearchParams(url.search);

  params.set('sp', uppProjectId);

  // URL mit aktualisierten Parametern ohne Reload setzen
  const newUrl = `${getDomainCfg().viewSettings.editorView}?${params.toString()}`;
  console.log('setting new url', newUrl);
  window.history.pushState({ path: newUrl }, '', newUrl);
  initializeHistoryHandler();

  const appCfg = getAppCfg();
  appCfg.projectId = uppProjectId;
  appCfg.view = "editor";
  //appCfg.code = code;
  setAppCfg(appCfg);

  await loadProject(false);
  const newEditorState = getViewData(EDITOR_VIEW_NAME);
  //console.log('new editor state', newEditorState);
  newEditorState.data = {
    ...newEditorState.data,
    //editorCode: code,
    chatMessages: prepareChatMessagesForProject(llmAnswer, query),
    activeTab: 'support'
  }
  newEditorState.project = {
    ...newEditorState.project,
    //botName: editorState.project.botName,
    //workflow : editorState.project.workflow,
    //definition: editorState.project.definition,
    projectOrigin: newEditorState.project.projectOrigin,
    project: llmAnswer.projectName,
  };
  //console.log('prepared editor state', newEditorState);
  setViewData(EDITOR_VIEW_NAME, newEditorState);
  saveUserData();
  triggerIntegrationStyles();
}

export async function showNewProject(lang: string, code: string): Promise<void> {
  const editorState = getViewData(EDITOR_VIEW_NAME);
  showLoadingView();
  const newProjectId = await getNewTemplateProjectId(lang.toUpperCase());
  const uppProjectId = newProjectId.toUpperCase();

  const url = new URL(window.location.href);
  const params = new URLSearchParams(url.search);
  params.set('sp', uppProjectId);

  // URL mit aktualisierten Parametern ohne Reload setzen
  const newUrl = `${url.pathname}?${params.toString()}`;
  window.history.pushState({ path: newUrl }, '', newUrl);
  initializeHistoryHandler();

  const appCfg = getAppCfg();
  appCfg.projectId = uppProjectId;
  //appCfg.code = code;
  setAppCfg(appCfg);

  await loadProject(false);
  const newEditorState = getViewData(EDITOR_VIEW_NAME);

  newEditorState.data = {
    ...newEditorState.data,
    editorCode: code,
    chatMessages: editorState?.data?.chatMessages,
  }
  newEditorState.project = {
    ...newEditorState.project,
    botName: editorState.project.botName,
    workflow: editorState.project.workflow,
    definition: editorState.project.definition,
    projectOrigin: newEditorState.project.projectOrigin,
    project: "Coding: " + editorState.project.project,
  };
  setViewData(EDITOR_VIEW_NAME, newEditorState);
  saveUserData();
  triggerIntegrationStyles();
}

export function initializeHistoryHandler() {
  window.addEventListener('popstate', async () => {
    const queryParams = await getQueryParams();
    if (Object.keys(queryParams).length > 0) {
      await loadData(false); // Assuming false for publicProject, adjust if needed
    }
  });
}

export async function loadProject(publicProject: boolean): Promise<void> {
  const projectId = getAppCfg().projectId;
  if (!projectId) {
    return showError('NO_PROJECT_GIVEN');
  }

  if (!isCloudProjectId(projectId)) {
    const projectUrl: string = createProjectUrl(projectId);
    console.log(projectId)
    const projectData = await loadProjectJson(projectUrl);
    (projectData as any).id = projectId;
    await loadProjectContent(projectData, false);
    const viewForProject = getViewNameForProject(projectData);
    console.log("found view for project", viewForProject);
    if (viewForProject) {
      if (viewForProject === "editor" && getAppCfg().view) {
        return showView(getAppCfg().view);
      }
      return showView(viewForProject);
    } else {
      return showView(getAppCfg().view);
    }
  } else {
    const cloudProject = getProjectMetaFromCloudId(projectId);
    const projectUrl: string = createProjectUrl(cloudProject.projectId);
    const projectData = await loadProjectJson(projectUrl);
    (projectData as any).id = projectId;
    await loadProjectContent(projectData, true);
    if (!getAppCfg().view) {
      showError('PROJECT_NOT_FOUND');
    } else {
      if (projectData.language === "chat") {
        showView("chat");
      } else {
        showView(getAppCfg().view);
      }
    }
  }
}

async function loadQuest() {
  const questId = getAppCfg().quest;
  if (!questId) {
    return showError('NO_PROJECT_GIVEN');
  }

  const questData = await getQuestDataForId(questId);
  showLoadingView();
  console.log('quest data:', questData)
  await loadQuestData(questData);
}

export async function loadProjectForLecture(projectJson: string) {
  const projectId = getAppCfg().projectId;
  const projectUrl: string = createProjectUrl(projectId);
  const projectData = await loadProjectJson(projectUrl, projectJson);
  (projectData as any).id = projectId;
  await loadEditorProject(projectData, false);
}

async function loadProjectContent(projectData: ProjectData, publicProject: boolean) {
  if (projectData === undefined || projectData.invalid) {
    showError('PROJECT_NOT_FOUND')
    return;
  }

  if (projectData.language === undefined && projectData.type === 'lecture') {
    showLoadingView();
    await loadInteractiveLecture(projectData);
  } else if (projectData.language === undefined || projectData.type === 'content') {
    showLoadingView();
    await loadContentProject(projectData);
  } else if (projectData.language || projectData.type === 'editor') {
    showLoadingView();
    await loadEditorProject(projectData, publicProject);
  }
}

function getViewNameForProject(projectData: ProjectData) {
  if (!projectData || projectData.invalid || hasError()) return ERROR_VIEW_NAME;
  if (projectData.language === "chat" || projectData.type === 'chat') return CHAT_VIEW_NAME;
  if (projectData.language || projectData.type === 'editor') return EDITOR_VIEW_NAME;
  else if (projectData.language === undefined && projectData.type === 'lecture') return LECTURE_VIEW_NAME;
  else if (projectData.language === undefined || projectData.type === 'content') return CONTENT_VIEW_NAME;

  return getAppCfg().view;
}

function isViewWithProject() {
  if (!getAppCfg().view) return true;
  const view = getAppCfg().view
  return (view === "content"
    || view === "launch"
    || view === "embed"
    || view === "editor"
    || view === "lecture"
    || view === "documentation"
    || view === "data"
    || view === "chat"
    || getAppCfg().projectId)
}

function isViewWithProjectList() {
  const view = getAppCfg().view
  return (view === "list" || view === "portal")
}

function isViewWithQuest() {
  console.log('is quest view?', getAppCfg().view === "quest")
  const view = getAppCfg().view
  return (view === "quest")
}

export async function resetCurrentProjectData(): Promise<void> {
  const editorState = getViewData(EDITOR_VIEW_NAME);

  const newState = { ...editorState };
  newState.data = {
    ...editorState.data,
    editorCode: editorState?.data?.projectContent?.projectSource,
    editorTabs: undefined,
    executionCode: getStartCode(editorState),
    editorDirty: false,
    projectFinished: false,
    projectProgress: undefined,
    rightTab: editorState.data?.rightTab, // Explizit erhalten, damit Chat-Tab nicht verloren geht
    v2EditorChanges: {}, // V2-Laufzeit-Änderungen leeren
  }

  if (shouldUseVFS(editorState.project)) {
    const projectId = editorState?.project?.id || getAppCfg().projectId || editorState?.project?.project?.replace(/\s+/g, '_');
    if (projectId) {
      console.log(`[resetCurrentProjectData] Deleting VFS data for project: ${projectId}`);
      await vfs.deleteProject(projectId);
    }

    if (newState.data?.projectContent) {
      newState.data.projectContent = {
        ...newState.data.projectContent,
        tabs: [] // Overwrite tabs to empty. Reload will fetch correct ones from projectJson
      }
    }
  }

  deleteUserCodeFromLS();
  setViewData(EDITOR_VIEW_NAME, newState);
  saveUserData();
}
